import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Lock, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import { completeInvite, getInvitePreview } from '../services/auth.service';

const completeInviteSchema = z.object({
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain uppercase letter')
    .regex(/[a-z]/, 'Password must contain lowercase letter')
    .regex(/[0-9]/, 'Password must contain number')
    .regex(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain special character'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

type CompleteInviteFormData = z.infer<typeof completeInviteSchema>;

const InviteComplete: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { applyAuthPayload } = useAuth();
  const [tokenError, setTokenError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [inviteName, setInviteName] = useState('');

  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CompleteInviteFormData>({
    resolver: zodResolver(completeInviteSchema),
  });

  useEffect(() => {
    const loadPreview = async () => {
      if (!token) {
        setTokenError(t('pages.inviteComplete.errors.noToken', 'Invalid or missing invitation link'));
        return;
      }

      try {
        setIsPreviewLoading(true);
        const preview = await getInvitePreview(token);
        setUserEmail(preview.email);
        setInviteName(preview.name || '');
      } catch (err: any) {
        setTokenError(
          err.response?.data?.message ||
          t('pages.inviteComplete.errors.tokenExpired', 'Invitation link is invalid')
        );
      } finally {
        setIsPreviewLoading(false);
      }
    };

    loadPreview();
  }, [token, t]);

  const onSubmit = async (data: CompleteInviteFormData) => {
    if (!token) {
      setSubmitError(t('pages.inviteComplete.errors.tokenExpired', 'Invitation link is invalid'));
      return;
    }

    try {
      setIsLoading(true);
      setSubmitError('');
      const response = await completeInvite(token, data.password);
      applyAuthPayload(response);
      setIsCompleted(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      setSubmitError(
        err.response?.data?.message ||
        t('pages.inviteComplete.errors.submit', 'Failed to complete registration')
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <div className="w-full max-w-md">
          <Card className="border-red-200 dark:border-red-900">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900">
                <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <CardTitle className="text-red-900 dark:text-red-100">
                {t('pages.inviteComplete.errors.title', 'Invalid Invitation')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-sm text-muted-foreground">{tokenError}</p>
              <Button asChild className="w-full" variant="outline">
                <Link to="/login">{t('pages.inviteComplete.errors.backToLogin', 'Back to Login')}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <div className="w-full max-w-md">
          <Card className="border-green-200 dark:border-green-900">
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
                <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <CardTitle className="text-green-900 dark:text-green-100">
                {t('pages.inviteComplete.success.title', 'Welcome!')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-center text-sm text-muted-foreground">
                {t(
                  'pages.inviteComplete.success.description',
                  'Your account has been created successfully. Redirecting...'
                )}
              </p>
              <div className="flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-orange-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <div className="w-full max-w-md space-y-6">
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
              <Lock className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle>{t('pages.inviteComplete.title', 'Complete Registration')}</CardTitle>
            <CardDescription>
              {t(
                'pages.inviteComplete.description',
                'Set your password to activate your account'
              )}
            </CardDescription>
            {userEmail && (
              <p className="text-xs text-muted-foreground mt-1">{userEmail}</p>
            )}
          </CardHeader>
          <CardContent>
            {submitError && (
              <Alert variant="error" className="mb-4">
                {submitError}
              </Alert>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label>{t('pages.inviteComplete.form.name', 'Your Name')}</Label>
                <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-foreground">
                  {isPreviewLoading
                    ? t('common.loading', 'Loading...')
                    : inviteName || t('pages.inviteComplete.form.namePlaceholder', 'John Doe')}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">
                  {t('pages.inviteComplete.form.password', 'Password')} *
                </Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  {...register('password')}
                  disabled={isLoading || isPreviewLoading}
                />
                {errors.password && <p className="text-xs text-red-500">{errors.password.message}</p>}
                <p className="text-xs text-muted-foreground">
                  {t('common.passwordRequirements')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  {t('pages.inviteComplete.form.confirmPassword', 'Confirm Password')} *
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  {...register('confirmPassword')}
                  disabled={isLoading || isPreviewLoading}
                />
                {errors.confirmPassword && (
                  <p className="text-xs text-red-500">
                    {errors.confirmPassword.message ||
                      t('pages.inviteComplete.form.passwordMismatch', 'Passwords do not match')}
                  </p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={isLoading || isPreviewLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isPreviewLoading && !isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('pages.inviteComplete.form.submit', 'Create Account')}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-muted-foreground">
          {t(
            'pages.inviteComplete.privacy',
            'By creating an account, you agree to our Terms of Service and Privacy Policy'
          )}
        </p>
      </div>
    </div>
  );
};

export default InviteComplete;
